import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  styled,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from "@mui/icons-material/Check";
import Navbar from "../../components/navbar";
import RazorpayService from "../razorpayService";
import useTestStore from "../../zustand/store";
import { useAuth } from "../../contexts/AuthContext";

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  marginBottom: theme.spacing(4),
}));

const PayButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  backgroundColor: "#F37254",
  color: "white",
  "&:hover": {
    backgroundColor: "#e65d3e",
  },
  "&.Mui-disabled": {
    backgroundColor: "#F37254",
    opacity: 0.7,
  },
}));

const ResultIcon = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 24px",
  "& svg": {
    fontSize: 40,
    color: "white",
  },
}));

const CheckOutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [transactionDetails, setTransactionDetails] = useState(null);
  const { user } = useAuth();
  const [alreadyBought, setAlreadyBought] = useState(false);
  // console.log(formData);

  const handlePayment = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      setPaymentStatus(null);

      await RazorpayService.initializeCounsellingPayment({
        onSuccess: (paymentDetails) => {
          console.log("Payment successful:", paymentDetails);
          setPaymentStatus("success");
          setTransactionDetails(paymentDetails);
        },
        onError: (error) => {
          if (error.status === 405) {
            setAlreadyBought(true);
          }
          console.error("Payment error:", error);
          setPaymentStatus("failed");
          setErrorMessage(error.message);
        },
        userData: { ...user },
      });
    } catch (error) {
      console.error("Checkout error:", error);
      setPaymentStatus("failed");
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetCheckout = () => {
    setPaymentStatus(null);
    setErrorMessage("");
    setTransactionDetails(null);
  };

  const features = [
    "Complete Assessment",
    "Detailed Career & Skill Report",
    "Expert Analysis",
    "Actionable Career Roadmap",
  ];

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontFamily: "Aclonica",
            fontSize: { xs: "40px", md: "50px" },
            mb: 4,
          }}
        >
          Check-Out
        </Typography>

        {!paymentStatus && (
          <StyledPaper>
            <Box
              sx={{
                p: 2,
                bgcolor: "rgba(151, 215, 226, 0.1)",
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6">Counselling Service</Typography>
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                >
                  Rs.499
                </Typography>
              </Box>
            </Box>

            <List>
              {features.map((feature, index) => (
                <ListItem key={index} sx={{ py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>

            <PayButton
              variant="contained"
              onClick={handlePayment}
              disabled={loading}
              startIcon={
                loading && <CircularProgress size={20} color="inherit" />
              }
            >
              {loading ? "Processing..." : "Pay Now"}
            </PayButton>
          </StyledPaper>
        )}

        {paymentStatus === "success" && (
          <StyledPaper sx={{ textAlign: "center" }}>
            <ResultIcon sx={{ bgcolor: "#48BB78" }}>
              <CheckCircleIcon />
            </ResultIcon>
            <Typography variant="h4" gutterBottom>
              Payment Successful!
            </Typography>
            <Typography color="textSecondary" paragraph>
              Thank you for your payment. Your transaction was successful.
            </Typography>

            {transactionDetails && (
              <Box sx={{ my: 3, textAlign: "left" }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Transaction ID: {transactionDetails.transactionId}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Order ID: {transactionDetails.orderId}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Amount Paid: ₹{transactionDetails.amount}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Date:{" "}
                  {new Date(transactionDetails.timestamp).toLocaleString()}
                </Typography>
                <Divider sx={{ mt: 2 }} />
              </Box>
            )}

            <Box
              sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/areaType-info")}
                sx={{
                  borderColor: "#F37254",
                  color: "#F37254",
                  "&:hover": {
                    borderColor: "#e65d3e",
                    color: "#e65d3e",
                  },
                }}
              >
                Go to next step
              </Button>
            </Box>
          </StyledPaper>
        )}

        {paymentStatus === "failed" && (
          <StyledPaper sx={{ textAlign: "center" }}>
            <ResultIcon sx={{ bgcolor: alreadyBought ? "#48BB78" : "#F56565" }}>
              {alreadyBought ? <CheckCircleIcon /> : <CancelIcon />}
            </ResultIcon>
            <Typography variant="h4" gutterBottom>
              {alreadyBought ? "No need to pay again" : "Payment Failed"}
            </Typography>
            <Typography color="textSecondary" paragraph>
              {alreadyBought
                ? "You have already bought the counselling!"
                : errorMessage ||
                  "We're sorry, but your payment could not be processed."}
            </Typography>

            <Box
              sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}
            >
              {!alreadyBought && (
                <Button
                  variant="contained"
                  onClick={resetCheckout}
                  sx={{
                    bgcolor: "#F37254",
                    "&:hover": { bgcolor: "#e65d3e" },
                  }}
                >
                  Try Again
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => navigate("/")}
                sx={{
                  borderColor: "#F37254",
                  color: "#F37254",
                  "&:hover": {
                    borderColor: "#e65d3e",
                    color: "#e65d3e",
                  },
                }}
              >
                Back to Home
              </Button>
            </Box>
          </StyledPaper>
        )}
      </Container>
    </Box>
  );
};

export default CheckOutPage;
